import * as envConfig from '../../modules/authentication/config';

export interface AuthService {
    tryGetAccessToken(): Promise<[boolean, unknown]>;
    fetchServerSideToken?(token: string): Promise<string>;
}

interface TokenResponse {
    feToken: string;
    obToken: string;
    expiresAt: number; // ms
}

const CACHE_TTL_MS = 55 * 60 * 1000; // ~55 minuti
const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // Ogni 50 minuti

export class AuthTokenCache {
    private cachedToken: TokenResponse | null = null;
    private isRefreshing = false;
    private pendingRequests: Array<(token: string | null) => void> = [];
    private refreshTimer: ReturnType<typeof setInterval> | null = null;

    constructor(private authService: AuthService) {
        this.startAutoRefresh();
    }

    private startAutoRefresh(): void {
        if (this.refreshTimer) clearInterval(this.refreshTimer);

        this.refreshTimer = setInterval(async () => {
            console.log("[AuthTokenCache] Avvio auto-refresh del token...");
            await this.getToken(); // Forza il refresh se necessario
        }, REFRESH_INTERVAL_MS);
    }

    private isTokenValid(): boolean {
        return this.cachedToken !== null && Date.now() < this.cachedToken.expiresAt;
    }

    private async refreshToken(): Promise<string | null> {
        if (this.isRefreshing) {
            return new Promise(resolve => {
                this.pendingRequests.push(resolve);
            });
        }

        this.isRefreshing = true;

        try {
            const [feSuccess, feResult] = await this.authService.tryGetAccessToken();
            if (!feSuccess || typeof feResult !== 'string') {
                throw new Error(`Failed to get FE token: ${feResult}`);
            }

            let obToken = feResult;

            if (this.authService.fetchServerSideToken) {
                const enableServerSideToken = envConfig.environment[window.location.host]?.enableServerSideToken;
                if (enableServerSideToken) {
                    obToken = await this.authService.fetchServerSideToken(feResult);
                }
            }

            this.cachedToken = {
                feToken: feResult,
                obToken,
                expiresAt: Date.now() + CACHE_TTL_MS,
            };

            this.resolvePendingRequests(obToken);
            return obToken;
        } catch (error) {
            console.error("[AuthTokenCache] Errore durante il refresh del token:", error);
            this.clearCache();
            return null;
        } finally {
            this.isRefreshing = false;
        }
    }

    private resolvePendingRequests(token: string | null): void {
        this.pendingRequests.forEach(cb => cb(token));
        this.pendingRequests = [];
    }

    public async getToken(): Promise<string | null> {
        if (this.isTokenValid()) {
            return this.cachedToken?.obToken ?? null;
        }

        return this.refreshToken();
    }

    public clearCache(): void {
        this.cachedToken = null;
        this.isRefreshing = false;
        this.pendingRequests = [];

        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }
    }
}